import type { JobData } from '../types/optimization';
import { analyzeResumeDensity } from './resumeAnalysisUtils';
import type { GapAnalysis } from './jdAnalysis';
import { formatGapAnalysisForPrompt, extractJDStructure } from './jdAnalysis';

// ── Canonical section title → ATS-standard header ────────────────────────────
const SECTION_HEADER_MAP: Record<string, string> = {
  'Personal Information':   'CONTACT INFORMATION',
  'Contact':                'CONTACT INFORMATION',
  'Contact Information':    'CONTACT INFORMATION',
  'Professional Summary':   'PROFESSIONAL SUMMARY',
  'Summary':                'PROFESSIONAL SUMMARY',
  'Objective':              'PROFESSIONAL SUMMARY',
  'Work Experience':        'WORK EXPERIENCE',
  'Experience':             'WORK EXPERIENCE',
  'Employment':             'WORK EXPERIENCE',
  'Employment History':     'WORK EXPERIENCE',
  'Education':              'EDUCATION',
  'Skills':                 'SKILLS',
  'Technical Skills':       'SKILLS',
  'Core Competencies':      'SKILLS',
  'Languages & Frameworks': 'LANGUAGES',
  'Languages':              'LANGUAGES',
  'Technical Proficiencies':'LANGUAGES',
  'Certifications':         'CERTIFICATIONS',
  'Certificates':           'CERTIFICATIONS',
  'Awards':                 'AWARDS & HONORS',
  'Awards & Honors':        'AWARDS & HONORS',
  'Honors':                 'AWARDS & HONORS',
  'Achievements':           'AWARDS & HONORS',
  'Projects':               'PROJECTS',
  'Additional Information': 'ADDITIONAL INFORMATION',
  'References':             'REFERENCES',
};

const CONTACT_TITLES = new Set(['Personal Information', 'Contact', 'Contact Information']);

/**
 * Reconstruct a clean, properly-ordered, ATS-structured text from parsed resume sections.
 *
 * CRITICAL: This is the text that is sent to the LLM for optimization.
 * Always prefer this over the raw `parsedText` from PDF extraction, which may
 * be jumbled when the source was a two-column PDF.
 *
 * @param sections  Array of { title, content } from the parsing pipeline
 * @returns         Structured plain text ready to send to the optimizer
 */
export function reconstructStructuredResumeText(
  sections: Array<{ title: string; content?: string; preview?: string }>
): string {
  if (!Array.isArray(sections) || sections.length === 0) return '';

  const parts: string[] = [];

  for (const section of sections) {
    const title   = (section.title   || '').trim();
    const content = (section.content || section.preview || '').trim();
    if (!content) continue;

    const header = SECTION_HEADER_MAP[title] ?? title.toUpperCase();

    if (CONTACT_TITLES.has(title)) {
      // Contact block: no section header, just the contact lines at the top
      parts.push(content);
    } else {
      parts.push(`${header}\n${content}`);
    }
  }

  return parts.join('\n\n');
}

export interface OptimizationPreferences {
  mode?: 'conservative' | 'balanced' | 'aggressive';
  focusAreas?: string[];
  preserveOriginalStyle?: boolean;
  enhanceKeywords?: boolean;
  improveFormatting?: boolean;
  enhanceContent?: boolean;
  improveStructure?: boolean;
  writingTone?: 'professional' | 'casual' | 'executive';
  detailLevel?: 'concise' | 'detailed' | 'comprehensive';
  /** When true, use chunked pipeline (parse → analyze → improve chunks → merge) instead of full-resume rewrite */
  useChunkedPipeline?: boolean;
  /** Pre-computed gap analysis — drives targeted optimization instructions */
  gapAnalysis?: GapAnalysis;
}

/**
 * Builds an optimization prompt based on resume text, job data, and experience level
 * @param resumeText The resume text to optimize
 * @param jobData The job data to optimize for
 * @param experienceLevel The detected experience level
 * @param preferences User optimization preferences
 * @returns Formatted prompt for LLM
 */
export function buildOptimizationPrompt(
  resumeText: string,
  jobData: JobData,
  experienceLevel: string,
  preferences?: OptimizationPreferences
): string {
  // Analyze content density
  const { density, expansionPotential } = analyzeResumeDensity(resumeText);
  const isDenseResume = density > 12;
  const isSparseResume = density < 8;
  
  const levelConfig = {
    Senior: {
      length: "2-3 pages",
      focus: "strategic leadership and business impact",
      sections: ["Executive Summary", "Leadership Impact", "Technical Governance"],
      expansionFactor: isSparseResume ? 1.8 : isDenseResume ? 1.1 : 1.4
    },
    "Mid-Level": {
      length: "1-2 pages", 
      focus: "technical leadership and project ownership",
      sections: ["Technical Skills", "Key Projects", "Achievements"],
      expansionFactor: isSparseResume ? 1.6 : isDenseResume ? 1.05 : 1.3
    },
    Junior: {
      length: "1 page",
      focus: "skills and learning agility",
      sections: ["Education", "Core Competencies", "Projects"],
      expansionFactor: isSparseResume ? 1.4 : isDenseResume ? 1.0 : 1.2
    }
  };

  const config = levelConfig[experienceLevel as keyof typeof levelConfig] || levelConfig["Mid-Level"];

  // Calculate adaptive content targets
  const originalWordCount = resumeText.split(/\s+/).length;
  const targetWordCount = Math.round(originalWordCount * config.expansionFactor);
  
  // Extract key skills and requirements for optimization context (NOT for insertion)
  const keySkills = jobData.skills?.slice(0, 10).join(', ') || 'general professional skills';
  const keyRequirements = jobData.requirements?.slice(0, 5).join(', ') || 'standard job requirements';

  // Job description excerpt for terminology and phrasing (within token budget; do not copy into resume)
  const MAX_JOB_DESC_CHARS = 2200; // Reduced slightly to make room for gap analysis
  const rawDesc = (jobData.description || '').trim();
  const jobDescriptionExcerpt = rawDesc.length > MAX_JOB_DESC_CHARS
    ? rawDesc.slice(0, MAX_JOB_DESC_CHARS) + '\n[... truncated for length]'
    : rawDesc;
  const hasJobExcerpt = jobDescriptionExcerpt.length >= 50;

  // Gap analysis — use pre-computed from llmOptimizationService (preferred) or skip
  const gapAnalysis: GapAnalysis | undefined = preferences?.gapAnalysis;
  const gapBlock = gapAnalysis
    ? formatGapAnalysisForPrompt(extractJDStructure(jobData), gapAnalysis)
    : '';
  const targetRoleLine = [jobData.title, jobData.company].filter(Boolean).join(' at ') || 'target role';

  // Apply user preferences
  const mode = preferences?.mode || 'balanced';
  const writingTone = preferences?.writingTone || 'professional';
  const detailLevel = preferences?.detailLevel || 'detailed';
  
  // Tone mapping
  const toneInstructions = {
    professional: 'Use formal, corporate language with industry-standard terminology',
    casual: 'Use approachable, conversational tone while maintaining professionalism',
    executive: 'Use sophisticated, C-level language emphasizing strategic impact and leadership'
  };
  
  // Detail level mapping
  const detailInstructions = {
    concise: 'Keep descriptions brief and to-the-point, focusing only on key achievements',
    detailed: 'Provide comprehensive descriptions with context and measurable results',
    comprehensive: 'Include extensive details, full context, and thorough explanations of all achievements'
  };
  
  // Mode-specific instructions
  const modeInstructions = {
    conservative: 'Make minimal changes, preserve 90% of original phrasing and structure',
    balanced: 'Optimize thoughtfully, maintaining original voice while enhancing clarity and impact',
    aggressive: 'Maximize ATS optimization with significant rewording and restructuring for best results'
  };

  return `
=== YOUR ROLE: OPTIMIZE FOR ATS + HUMAN READERS (CORE VALUE) ===
You are the resume optimizer. Your output must deliver real value in two ways:

1. ATS value: Use clear structure (headings, dates, company names), standard formatting, and semantic richness. Modern ATS use NLP—they understand meaning and context, not just keyword count. Align the candidate's existing experience with the job description's terminology where it is truthful (rephrase, same facts). Clear structure helps parsing; meaningful bullets (how skills were used, context and outcome) help semantic match. No keyword stuffing—quality and proof in experience over quantity.

2. Human value: Recruiters spend seconds per resume. Write so they immediately see who this person is, where they worked, and how they fit this role. Use natural language and specific bullets (action + context + outcome). The summary should speak to the target role; bullets should tell a short story, not list technologies. Avoid generic or AI-sounding phrases. The resume should feel like a strong, human-tailored application for this job—not a template with keywords pasted.

You have the candidate's real history. Your job is to reframe it in the job's language, preserve every fact and every role, and present it so both systems and humans see a clear fit. Do not invent, merge roles, or embellish. Every improvement must be rephrasing or clearer structure, never new content.

=== AUTHENTICITY & TRUTHFULNESS (STRICT) ===
1. Do NOT add any skills, tools, certifications, or achievements not already present in the original resume text.
2. Do NOT fabricate, guess, or infer job titles, company names, employment dates, or project details.
3. Preserve all factual details exactly as given, including work experience, education credentials, and certifications.
4. Do NOT insert any target job posting URL, company name, or job title from the job description into the resume body.
5. Do NOT include phrases such as "Here is your resume", "Based on the job description", or "Note from the assistant".
6. Do NOT alter, add, remove, or fabricate ANY contact details (email, phone, address, LinkedIn, portfolio).
7. If contact details are missing in the original resume, leave them BLANK or omit them entirely - DO NOT create fake ones.
8. Achievements must remain factually accurate; only expand for clarity and emphasis. Do NOT invent metrics (e.g. "25% increase", "30% reduction") or any percentages/numbers that are not explicitly in the original.
9. NEVER add example data, placeholder text, or generic information not in the original resume.
10. Do NOT reframe education or certifications as work achievements (e.g. if the original lists "Digital Marketing NanoDegree" as a certification, do NOT write "Led the development of a digital marketing program with X% results").
11. Preserve the original number of years of experience and any other factual numbers (e.g. "18 years", "15 years in telecom") in the summary; do not replace with different numbers.

=== OUTPUT CONTROL ===
1. Begin immediately with the candidate's name and contact information.
2. End immediately after the last resume section ΓÇö no extra notes, disclaimers, or commentary.
3. Do NOT include any introductory or closing sentences.
4. Maintain resume as plain text or Markdown only ΓÇö no images, tables, or decorative elements.
5. Do NOT add "Generated by GoGrabJob" or any meta-content within the resume.

=== FORMATTING RULES (ATS-FRIENDLY) ===
- CRITICAL STRUCTURE: Put each item on its own line. One line for name, one per contact field (email on its own line, phone on its own line, location on its own line, LinkedIn on its own line). One line per bullet point. Put each section heading on its own line with a blank line before it. Use newlines; do NOT run multiple contact fields, sections, or bullets together on one line.
- Use section headings in ALL CAPS (e.g., PROFESSIONAL SUMMARY, EXPERIENCE).
- CRITICAL: Section headings that contain multiple words (e.g. PROFESSIONAL SUMMARY, WORK EXPERIENCE, ADDITIONAL INFORMATION) MUST be written on a SINGLE line. Never break a section heading across two lines.
- Keep a single-column layout.
- Contact information format (USE ONLY what's in the original resume):
  Full Name
  email@address.com
  +1-555-000-0000
  City, State ZIP
  https://linkedin.com/in/username

  CRITICAL: Each contact field on its own separate line. ABSOLUTELY NO bullet characters (•, -, *, +) before contact lines. If any contact field is missing in the original, omit it entirely. Do NOT add placeholder text.
- Experience entries:
  Job Title, Company Name (MM/YYYY – MM/YYYY)
  • Bullet starting with action verb
  • Include measurable metrics ONLY when they appear in the original resume; do NOT invent percentages or revenue/cost figures.
  CRITICAL: Preserve EVERY work experience entry. Do NOT merge roles. Do NOT drop any company name, job title, or date range.
- Bullet points:
  * Use standard markdown bullets (• or -) ONLY
  * Do NOT use + symbols for bullet points
  * Max 2 lines each
  * Consistent indentation
- Skills section — CONSOLIDATION RULE (CRITICAL):
  * ALL technical skills, tools, frameworks, languages, cloud technologies, AI/ML tools, methodologies, and any other skill sub-categories MUST be consolidated into ONE single "SKILLS" section in the output.
  * Do NOT create separate top-level sections for "Languages & Frameworks", "Cloud Technologies", "AI/ML Technologies", "Product Management Tools", "Methodologies", "Technologies", "Frameworks", or any other skill sub-category. They ALL go inside the SKILLS section.
  * Format: Use short grouped lines within the SKILLS section (e.g. "Languages: Java, Python, Go" on one line, "Cloud: Kubernetes, Docker" on the next). Each group on its own line.
  * Include ONLY skills that appear in the original resume. Do NOT add new ones.
  * Do NOT dump a long list of 30+ technologies on one line.
  * In EXPERIENCE bullets, mention only 1–3 tools that were directly used in that specific role, not a full stack dump.
- Education: degree, institution, graduation year, reverse chronological order.
- One blank line between sections.

=== BULLET REWRITING (OPTIMIZATION CORE) ===
- CRITICAL: Every bullet in EXPERIENCE must follow: [Strong Action Verb] + [What you did] + [Context/Scale] + [Outcome if stated in original].
- Replace ALL weak opening phrases: "Responsible for", "Worked on", "Helped with", "Assisted in", "Involved in", "Participated in", "Contributed to" → replace with a strong active verb (Led, Built, Designed, Delivered, Reduced, Increased, Automated, Launched, Managed, Architected, Streamlined).
- Rewrite vague bullets: "Utilized various technologies" → "Architected microservices using Kubernetes and Docker, reducing deployment lead time" (use context from the resume, not invented facts).
- Align bullet language with the job description's terminology where the experience truthfully supports it.
- Each bullet must describe an action, the context, and (where original has it) the result. Not a list of tools.

=== STRUCTURAL ORDER (PRESERVE USER + FILL GAPS) ===
- Standard order: 1) CONTACT INFORMATION 2) PROFESSIONAL SUMMARY 3) SKILLS 4) EXPERIENCE 5) EDUCATION 6) CERTIFICATIONS 7) AWARDS & HONORS.
- Preserve original section order and heading names. Do not rename or remove sections.
- If a standard section is missing, add it using only facts from the original resume.
- For junior candidates with limited work history, move EDUCATION above EXPERIENCE.

=== JOB ALIGNMENT (REPHRASE WITH JOB LANGUAGE) ===
- Rephrase the candidate's existing experience to use the job description's terminology and phrasing where it accurately describes the same experience. Same facts, job's words.
- Do NOT add or imply skills, achievements, or responsibilities not in the original resume. Only reframe what is already there.
- Prefer the job description's action verbs and recurring phrases when they truthfully apply (e.g. "stakeholder management" instead of "worked with partners" when the experience supports it).
${mode === 'aggressive' ? '- Prioritize wording that matches required and preferred qualifications when the resume supports it.' : ''}

=== SEMANTIC DEPTH & BULLETS ===
- For each bullet, show HOW the skill or responsibility was used (context and outcome), not just that it was used. Modern ATS use semantic search; meaning and context matter.
- Prefer 10 well-placed, relevant keywords over 30 forced ones. Quality over quantity. No keyword stuffing; no skills listed without proof in experience.
- Do NOT write experience bullets that are just a list of technologies (e.g. "Utilized Java, Python, Go, Kubernetes, Docker, Kafka, RAG, LangChain..."). Each bullet should describe an action, context, and outcome; mention 1–3 relevant tools where they were actually used in that context.
- Avoid generic or AI-sounding phrasing. Write like a strong human application for this specific role.
- Syntactic simplicity: short bullets, standard grammar, clear headers. Natural language for recruiters.

=== ATS & OPTIMIZATION GUIDELINES ===
- Reframe existing experience using the job's language where it accurately applies; do not add new facts.
- Expand sparse sections using context from existing experience.
- Condense dense sections by merging similar points and removing redundancy.
- Remove icons, images, or decorative dividers.
- Keep line length under 80 characters.
- Use metrics in EXPERIENCE bullets only when the original resume already states them; never invent percentages or growth figures.
- Ensure consistent date formatting: MM/YYYY ΓÇô MM/YYYY.
- Maintain consistent capitalization for job titles and section headings.

=== ETHICAL REQUIREMENTS ===
- No misrepresentation of history or credentials.
- No added degrees or certifications.
- Avoid unverifiable or exaggerated claims.
- Redact sensitive info if requested.
- Ensure compliance with anti-discrimination laws.

=== USER PREFERENCES ===
Optimization Mode: ${mode.toUpperCase()}
- ${modeInstructions[mode]}

Writing Tone: ${writingTone.toUpperCase()}
- ${toneInstructions[writingTone]}

Detail Level: ${detailLevel.toUpperCase()}
- ${detailInstructions[detailLevel]}

Focus Areas: ${preferences?.focusAreas?.join(', ').toUpperCase() || 'ALL AREAS'}

=== CONTENT SCALING INSTRUCTIONS ===
Experience Level: ${experienceLevel.toUpperCase()}
Content Density: ${density.toFixed(1)} (${isSparseResume ? 'SPARSE' : isDenseResume ? 'DENSE' : 'BALANCED'})
Original length: ${originalWordCount} words
Target length: ${targetWordCount} words (${config.expansionFactor}x)
Action: ${isSparseResume ? 'EXPAND content significantly' : isDenseResume ? 'CONDENSE content slightly' : 'ENHANCE content moderately'}

Optimization Context:
- TARGET ROLE: ${targetRoleLine}
- Target role requires: ${keyRequirements}
- Key skills to emphasize: ${keySkills}
${hasJobExcerpt ? `
JOB DESCRIPTION EXCERPT (use for terminology and phrasing when rephrasing the resume; do NOT copy sentences into the resume or add facts not in the original):
---
${jobDescriptionExcerpt}
---
` : ''}
${gapBlock ? `
${gapBlock}
` : ''}
For SPARSE sections (${density < 8 ? 'ALL' : 'marked'}):
- Expand bullet points with quantifiable achievements
- Add missing context from existing experience
- Target ${Math.round(config.expansionFactor * 100)}% length increase

For DENSE sections (${density > 12 ? 'ALL' : 'marked'}):
- Consolidate similar points
- Remove redundant information
- Target ${Math.round(100 - (100 / config.expansionFactor))}% length reduction

CRITICAL REMINDERS:
- Output the COMPLETE resume from start to finish.
- Include ALL sections present in the original: Contact, Summary, Skills, Experience, Education, Certifications, Awards (or whatever structure the original has). Preserve the original section structure; do not merge Education and Certifications if they were separate.
- Do NOT truncate or summarize any section. Do NOT merge multiple work roles into one.
- You MUST list EVERY work experience entry from the original resume, each with its correct job title, company name, and date range (MM/YYYY ΓÇô MM/YYYY). Do not drop or combine roles. Different users have different resumes; preserve each user's full history.
- Maintain all dates, companies, and achievements from the original. Do NOT add metrics (%, revenue, growth) that are not in the original.
- Output MUST be at least ${targetWordCount} words to ensure completeness.
- If you reach a token limit, prioritize completing all sections over verbose descriptions.

Now, rewrite the following resume according to these rules and maintain its truthfulness and ATS compliance.

REQUIRED JSON OUTPUT FORMAT:
Respond with a JSON object containing EXACTLY these two keys:
1. "optimizedResume" — the complete optimized resume as a single plain-text string (ALL CAPS section headers, no markdown, all sections preserved).
2. "sections" — an array of section objects, one per section, in canonical order. Each object:
   { "title": "<canonical title>", "content": "<section body text — NO section header line inside content>" }

Allowed "title" values (use EXACTLY these strings):
  "Personal Information", "Professional Summary", "Work Experience", "Education",
  "Skills", "Languages", "Certifications", "Awards & Honors", "Projects", "Additional Information"

Rules for "sections":
- "content" must contain ONLY the body text of that section (no header/title line).
- For "Work Experience": include all jobs. Each job: "Company | Job Title | MM/YYYY – MM/YYYY" then bullet lines.
- For "Skills": ALL skill sub-categories (languages, frameworks, cloud, AI/ML, tools, methodologies) MUST be merged into ONE "Skills" entry. Use grouped lines (e.g. "Languages: Java, Python\nCloud: Kubernetes, Docker"). Do NOT create separate section entries for skill sub-categories.
- For "Personal Information": name on first line, then exactly one contact field per line (email, phone, location, LinkedIn). NO bullet characters before contact lines.
- Omit sections that have no content. Preserve every section that has content.
- Do NOT add the section title inside the content string.
- CRITICAL: The "sections" array must have at most ONE entry with title "Skills". If the original resume had skill sub-categories as separate sections, consolidate them all into that single "Skills" entry.

Example structure:
{"optimizedResume":"Full Name\nEmail: ...\n\nPROFESSIONAL SUMMARY\n...\n\nWORK EXPERIENCE\n...","sections":[{"title":"Personal Information","content":"Full Name\nEmail: ...\nPhone: ..."},{"title":"Professional Summary","content":"Senior product leader..."},{"title":"Work Experience","content":"Company A | Role | 01/2020 – Present\n• Achievement one\n• Achievement two"}]}

ORIGINAL RESUME CONTENT:
${resumeText}
  `.trim();
}
